import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { graphql } from '@e-socna/data-layer';
import { EMPTY_ARRAY } from '@e-socna/empty';
import { buildPath } from '@e-socna/utils';

import { SplashScreen } from '../../components/SplashScreen';
import { isListedInItsFolder } from '../../app/utils/mission-kind-profile.util';
import { useGraphQLQuery } from '../../hooks/useGraphQLQuery.hook';
import { Loading } from '../../components/Loading';
import { SaveToBookmarks } from '../../components/Illustrations/SaveToBookmarks';
import { PATH_FOLDER_MISSIONS_NEW } from '../../app/constants/paths.constant';
import { FolderBusinessEntityList } from '../../components/FolderBusinessEntityList';
import { FolderBusinessEntityListVariant } from '../../components/FolderBusinessEntityList/enums/variant.enum';
import { useIdParam } from '../../utils/use-id-param.util';
import { useBuildPath } from '../../hooks/useBuildPath.hook';
import { SplitButton } from '../../components/SplitButton';
import { useFolderAccess } from '../../components/Access/hooks/useFolderAccess.hook';
import type { SplitButtonOption } from '../../components/SplitButton';

import {
  MISSION_CREATION_OPTIONS,
  MISSION_CREATION_PATH_BY_OPTION,
} from './constants/mission-creation-options.constant';

export const FolderMissionsPage_MissionsWithinAFolder_QueryDocument = graphql(`
  query FolderMissionsPage_MissionsWithinAFolder_Query($folderId: Id!) {
    findMissionsWithinAFolder(folderId: $folderId) {
      kind
      ...FolderBusinessEntityListCmp_FolderBusinessEntity_Fragment
    }
  }
`);

export const MissionsWithinAFolder = () => {
  const navigate = useNavigate();

  const folderId = useIdParam('folderId');
  const { initMission: creationGate } = useFolderAccess(folderId);

  const { data, isLoading } = useGraphQLQuery(
    FolderMissionsPage_MissionsWithinAFolder_QueryDocument,
    { folderId },
    { enabled: !!folderId },
  );

  const path = useBuildPath(PATH_FOLDER_MISSIONS_NEW, { folderId });

  const goToCreationPage = useCallback(
    () => void navigate(path),
    [navigate, path],
  );

  const goToCreationPageOf = useCallback(
    ({ key: creationOption }: Readonly<SplitButtonOption>) =>
      void navigate(
        buildPath(
          MISSION_CREATION_PATH_BY_OPTION[creationOption] ??
            PATH_FOLDER_MISSIONS_NEW,
          { folderId },
        ),
      ),
    [folderId, navigate],
  );

  if (isLoading) {
    return <Loading />;
  }

  const missions = (data?.findMissionsWithinAFolder ?? EMPTY_ARRAY).filter(
    (mission) => isListedInItsFolder(mission.kind),
  );

  if (!missions.length) {
    return (
      <SplashScreen
        illustration={<SaveToBookmarks />}
        message="Vous n'avez pas de mission dans ce dossier pour le moment. Commencez le travail !"
      >
        <SplitButton
          ariaLabelButton="Choisir le type à créer"
          ariaLabelButtonGroup="Créer une mission"
          gate={creationGate}
          id="folder-missions-empty-split-button"
          onClick={goToCreationPageOf}
          options={MISSION_CREATION_OPTIONS}
          size="large"
        />
      </SplashScreen>
    );
  }

  return (
    <FolderBusinessEntityList
      createGate={creationGate}
      createOptions={MISSION_CREATION_OPTIONS}
      folderBusinessEntities={missions}
      onCreateOption={goToCreationPageOf}
      onStartCreate={goToCreationPage}
      variant={FolderBusinessEntityListVariant.Cards}
    />
  );
};
